import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiClient } from '@/api/client'
import type { components } from '@/api/generated/schema'
import { percorsiMenuNovita } from '../lib/testiNovita'

export type Novita = components['schemas']['NovitaDTO']

/**
 * «Novità» della piattaforma (banner): gli annunci da mostrare ora all'utente.
 *
 * Il server decide tutto (interruttore generale, periodo, destinatari, funzione già attiva per l'azienda, annunci già
 * chiusi): qui si mostra il primo della lista, UNO alla volta. Con l'interruttore spento l'elenco è vuoto e non si vede
 * nulla. Lo stato è per utente: cambiando utente nella stessa scheda si ricarica da capo.
 */
export const useNovitaStore = defineStore('novita', () => {
  const elenco = ref<Novita[]>([])
  const utenteCaricato = ref<number | string | null>(null)

  /** Annunci chiusi in questa sessione (la chiusura vera sta sul server; qui si nasconde subito). */
  const chiusi = ref<Set<number>>(new Set())

  /** Eventi di statistica già segnalati in questa sessione («id:tipo»): una volta sola. */
  const segnalati = new Set<string>()

  /** L'annuncio da mostrare: uno solo alla volta, il primo non chiuso. */
  const corrente = computed<Novita | null>(() => elenco.value.find((a) => a.id != null && !chiusi.value.has(a.id)) ?? null)

  /** Percorsi delle voci di menu da marcare «Nuovo» (link degli annunci non chiusi). Vuoto con l'interruttore spento. */
  const percorsiMenu = computed<Set<string>>(() => percorsiMenuNovita(elenco.value, chiusi.value))

  /** Carica gli annunci dell'utente. Gli errori non si mostrano mai: una Novità mancante non è un problema per chi lavora. */
  async function carica(utenteId: number | string): Promise<void> {
    if (utenteCaricato.value !== utenteId) {
      elenco.value = []
      chiusi.value = new Set()
      segnalati.clear()
    }
    utenteCaricato.value = utenteId
    try {
      const { data } = await apiClient.get<Novita[]>('/api/v2/novita')
      if (utenteCaricato.value !== utenteId) return   // nel frattempo è cambiato l'utente
      elenco.value = Array.isArray(data) ? data : []
    } catch {
      if (utenteCaricato.value === utenteId) elenco.value = []
    }
  }

  /** Chiude un annuncio: sparisce subito; il server lo ricorda per questo account (idempotente). */
  async function chiudi(id: number | undefined): Promise<void> {
    if (id == null) return
    chiusi.value = new Set([...chiusi.value, id])
    try {
      await apiClient.post(`/api/v2/novita/${id}/chiudi`)
    } catch {
      // se la rete cade resta nascosto per questa sessione e tornerà al prossimo accesso: va bene così
    }
  }

  /**
   * Statistiche d'uso: segnala al server che l'annuncio è stato mostrato («visto») o che il suo pulsante è stato premuto
   * («cliccato»). Una sola volta per sessione, annuncio e tipo (il server comunque è idempotente); non blocca mai l'interfaccia
   * e non mostra mai errori. Azienda e utente li decide il server dal token.
   */
  function segnala(id: number | undefined, tipo: 'visto' | 'cliccato'): void {
    if (id == null) return
    const chiave = `${id}:${tipo}`
    if (segnalati.has(chiave)) return
    segnalati.add(chiave)
    void apiClient.post(`/api/v2/novita/${id}/${tipo}`).catch(() => {
      segnalati.delete(chiave)       // se la rete cade si riprova alla prossima occasione
    })
  }

  function azzera(): void {
    elenco.value = []
    chiusi.value = new Set()
    utenteCaricato.value = null
    segnalati.clear()
  }

  return { elenco, corrente, percorsiMenu, carica, chiudi, segnala, azzera }
})
